import {
  applaunchService,
  fido,
  freemiumService,
  gdprService,
  geoService,
  heartbeatService,
  heartbeatServiceV3,
  imageService,
  layoutForThirdPartyService,
  layoutService,
  oidcService,
  pairingService,
  parentalControlService,
  premiumService,
  profileService,
  programService,
  statsdMonitoringService,
  timeService,
  tokenService,
  upFrontTokenService,
  userService,
  videoService,
} from '@bedrock/fido-sdk';
import config from '@bedrock-rdk/config';
import { appVersion, appPlatform, getApplaunchConfig, getPubId } from '@bedrock-rdk/utils';
import { getCurrentMsTime } from './app.selectors';
import { getDeviceId, getHomeId, getLocalDeviceId, getLocalHomeId } from '../user/user.selectors';
import { patchOIDC } from './fido.oidc';

/**
 * Initialize fido SDK.
 *
 * @param {String} identifiers.Id
 * @param {Object} localId
 * @param {Function} getState
 */
export const initFidoSDK = getState => {
  const state = getState();

  const homeId = getHomeId(state);
  const localDeviceId = getLocalDeviceId(state);
  const localHomeId = getLocalHomeId(state);
  const deviceId = getDeviceId(state);

  const ID = homeId || deviceId;
  const boxId = homeId || deviceId;
  const localId = localHomeId ? { localHomeId } : { localDeviceId };
  const [localIdValue] = Object.values(localId);
  if (!(ID && localIdValue)) {
    throw new ReferenceError('The ID and localId are required to initialize fido.');
  }

  patchOIDC(oidcService);

  const {
    customerCode,
    platformCode,
    clientCode,
    fido: { baseUrlsByAPIType, saltAndSugar, imageSalt },
    oidc,
  } = config;

  const fidoConfig = {
    customerCode,
    platformCode,
    getCurrentTime: () => getCurrentMsTime(getState()),
    clientCode,
    saltAndSugar,
    imageSalt,
    appVersion,
    ...localId,
    baseUrlsByAPIType: {
      monitoring: 'https://mon.m6web.fr',
    },
  };

  const boxType = clientCode;
  const applaunchConfig = getApplaunchConfig(ID);

  const services = [
    [applaunchService, [applaunchConfig]],
    geoService,
    gdprService,
    heartbeatService,
    heartbeatServiceV3,
    imageService,
    programService,
    [statsdMonitoringService, [true, { salt: config.monitoring.salt, enabled: true, platformCode: appPlatform }]],
    timeService,
    [
      tokenService,
      [{ advertisingId: getPubId(), boxType, boxId, deviceModel: appPlatform, partnerContext: appPlatform }],
    ],
    userService,
    videoService,
    premiumService,
    freemiumService,
    upFrontTokenService,
    profileService,
    parentalControlService,
    [pairingService, [{ prefixedBoxId: true }]],
  ];

  if (oidc.baseUrl) {
    fidoConfig.baseUrlsByAPIType.oidc = oidc.baseUrl;
    fidoConfig.baseUrlsByAPIType.oidcCustomApi = oidc.customApi;
    services.push([
      oidcService,
      [
        {
          clientId: oidc.clientId,
          clientSecret: oidc.clientSecret,
          endpoints: oidc.endpoints,
        },
      ],
    ]);
  }

  fido.init(fidoConfig, services);
  fido.setBaseUrls(baseUrlsByAPIType);
};

export const updateFidoConfig = config => {
  const {
    customerCode,
    platformCode,
    clientCode,
    fido: {
      baseUrlsByAPIType,
      layoutService: { capacityToken },
      saltAndSugar,
      imageSalt,
    },
  } = config;

  const fidoConfig = {
    customerCode,
    platformCode,
    clientCode,
    saltAndSugar,
    imageSalt,
  };

  Object.entries(fidoConfig).forEach(([key, value]) => {
    fido.set(key, value);
  });
  fido.setBaseUrls(baseUrlsByAPIType);

  layoutService.init(capacityToken);
  layoutForThirdPartyService.init(capacityToken);
};
